/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, RefObject } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { GalleryTile, GalleryTileProps } from "./DefaultPromptTile";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { HiOutlineSparkles } from 'react-icons/hi';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import { setChatMode } from '../../store/chatModeSlice';

// Import types and constants
import { GalleryMode, MODES } from './data/galleryTypes';

// Import utility functions
import { 
  getTilesForMode,
  getTileDataForMode,
  getResponsiveMaxParentHeight,
  getMaxTiles
} from './data/galleryUtils';
import { useTheme } from '../Theme/ThemeProvider';
import { isMobileDevice } from '../../hooks/useDeviceDetection';

interface GalleryProps {
  current: { mode: GalleryMode | null; direction: number };
  goToModeWithDirection?: (mode: GalleryMode, direction: number) => void;
  exitDirectionRef: RefObject<number>;
  isAnimating: boolean;
  setIsAnimating: (value: boolean) => void;
  setExitDirectionRef?: RefObject<((direction: number) => void) | null>;
  chatInputHeight?: number;
  onTileClick?: (prompt: string, tool?: string) => void;
  deviceType: 'mobile' | 'tablet' | 'desktop';
}

/** Custom Gallery component that allows users to carousel through different modes (Ask Chat, 3M Knowledge) */
export function Gallery({ 
  current, 
  goToModeWithDirection, 
  exitDirectionRef, 
  isAnimating, 
  setIsAnimating, 
  setExitDirectionRef,
  chatInputHeight = 56,
  onTileClick,
  deviceType
}: GalleryProps) {
  const { mode, accent } = useTheme();
  const dispatch = useDispatch();
  
  // Map current accent and mode to text colors to match main content
  const getAccentTextColor = (accentColor: string, themeMode: string) => {
    if (themeMode === 'light') {
      // Light mode colors - match the --color-text values from CSS
      if (accentColor === 'purple') return '#4c1d95'; // purple-800 - matches light-purple theme
      if (accentColor === 'blue') return '#1e3a8a'; // blue-900 - matches light-blue theme  
      if (accentColor === 'green') return '#065f46'; // emerald-900 - matches light-green theme
      return '#1f2937'; // gray-800 for none themes in light mode
    } else {
      // Dark mode colors - match existing implementation
      if (accentColor === 'purple') return '#a78bfa'; // purple-400
      if (accentColor === 'blue') return '#60a5fa'; // blue-400  
      if (accentColor === 'green') return '#34d399'; // emerald-400
      return '#e5e7eb'; // gray-200 for none themes
    }
  };

  const titleColor = getAccentTextColor(accent, mode);
  
  // Track the baseline ChatInput height (56px) - but don't use it for tile height calculations
  const baseChatInputHeight = 56;
  const maxChatInputHeight = 128;
  // Remove chatInputGrowth calculations - we don't want tile height to change with input height
  
  const [tileSize, setTileSize] = useState({ width: 220, height: 180 });
  // Compact gallery tweaks
  const galleryExtraBottomPadding = 5; // minimal space below gallery for maximum tile height
  const galleryFixedWidth = 200; // further decreased fixed width for denser narrow layout
  const GALLERY_PADDING_INCREASE = 0; // removed extra padding to allow wider gallery
  const EXTRA_GALLERY_SIDE_PADDING = 20; // minimal side padding to allow wider gallery
  const tileWidthReduction = 10; // minimal reduction for two-column layout
  const tileHeightReduction = 0; // remove height reduction to allow taller tiles
  // Use a smaller max width at 150% zoom (devicePixelRatio >= 1.4) vs 100% zoom
  const tileMaxWidth = (typeof window !== 'undefined' && window.devicePixelRatio >= 1.4) ? 370 : 400;
  const minSubtitleHeight = 35; // lower threshold for showing subtitles
  const greetingVisualShift = 10; // minimal greeting space to allow more gallery height
  const ARROW_INSET_ADJUST = 32; // space between arrow and tile content start
  const ARROW_ICON_WIDTH = 40; // Chevron icon width - increased for better visibility
  const ARROW_SIDE_GAP = 12; // desired visual gap between arrow edge and tile column content
  // New layout compensation constants: account for header row and space below it that were not
  // previously included in tile height calculations, causing overflow.
  const headerBlockHeight = 38; // approximate combined height of section header (icon + text)
  const headerMarginBottom = 16; // mb-4 class = 16px margin-bottom
  const columnVerticalPadding = 0; // no additional padding needed since mb-4 handles the gap
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [responsivePadding, setResponsivePadding] = useState(180);
  const galleryRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null); // Ref to measure actual container height
  const isFirstRenderRef = useRef(true);
  const [isSizingComplete, setIsSizingComplete] = useState(false);
  const lastProcessedHeightRef = useRef<number>(chatInputHeight);
  const [containerHeight, setContainerHeight] = useState<number>(0); // Track actual container height
  
  // Calculate responsive padding based on window width
  const getResponsivePadding = useCallback(() => {
    const windowWidth = window.innerWidth;
    if (windowWidth >= 1400) return 160 + GALLERY_PADDING_INCREASE;
    if (windowWidth >= 1200) return 100 + GALLERY_PADDING_INCREASE;
    if (windowWidth >= 1100) return 70 + GALLERY_PADDING_INCREASE;
    if (windowWidth >= 1000) return 50 + GALLERY_PADDING_INCREASE;
    if (windowWidth >= 950) return 40 + GALLERY_PADDING_INCREASE;
    if (windowWidth >= 800) return 30 + GALLERY_PADDING_INCREASE;
    return 20 + GALLERY_PADDING_INCREASE;
  }, []);

  // Calculate adaptive tile width based on available space
  const calculateTileWidth = useCallback(() => {
    const windowWidth = window.innerWidth;
    const responsivePadding = getResponsivePadding();
    
    // Determine MainContent padding based on screen size
    // Updated to match new DesktopLayout padding: xl:px-[50px] 2xl:px-[100px] 3xl:px-[140px]
    // At 150% zoom, window widths are smaller so padding is minimal
    let mainContentPadding;
    if (windowWidth >= 1920) mainContentPadding = 140; // 3xl - large monitors at 100%
    else if (windowWidth >= 1536) mainContentPadding = 100; // 2xl - medium/large at 100% or large at 150%
    else if (windowWidth >= 1280) mainContentPadding = 50; // xl - small at 100% or medium at 150%
    else if (windowWidth >= 1024) mainContentPadding = 24; // lg
    else mainContentPadding = 8; // fallback
    
    // Available width = window width - parent padding (responsive * 2) - gallery padding (responsive * 2) - safety margin for border visibility
    const safetyMargin = windowWidth >= 1200 ? 40 : windowWidth >= 1000 ? 30 : 20;
    const availableWidth = windowWidth
      - (mainContentPadding * 2)
      - (responsivePadding * 2)
      - (EXTRA_GALLERY_SIDE_PADDING * 2)
      - safetyMargin;
    
    // Two column layout - divide by 2 and account for gap between columns
    const columnGap = 32; // gap between the two columns
    const tileSpacing = windowWidth >= 1200 ? 12 : windowWidth >= 1000 ? 10 : 8;
    const calculatedWidth = Math.floor((availableWidth - columnGap) / 2) - tileSpacing;
    
    // Responsive minimum width based on screen size
  let minWidth;
  if (windowWidth >= 1400) minWidth = 250;
  else if (windowWidth >= 1200) minWidth = 230;
  else if (windowWidth >= 1100) minWidth = 210;
  else if (windowWidth >= 1000) minWidth = 200;
  else if (windowWidth >= 950) minWidth = 190;
  else if (windowWidth >= 800) minWidth = 180;
  else minWidth = 170;
    
    return Math.min(tileMaxWidth, Math.max(minWidth, calculatedWidth));
  }, [getResponsivePadding, tileMaxWidth]);
  
  // Store the exit direction for animations - updated by flushSync when animations start
  const [exitDirection, setExitDirection] = useState(1);
  const previousModeRef = useRef<GalleryMode | null>(null);
    
  // Update the tracking flag in useEffect (after render)
  useEffect(() => {
    // No special first selection logic
  }, [current.mode]);

  // Expose setExitDirection to parent components via ref
  React.useEffect(() => {
    if (setExitDirectionRef) {
      setExitDirectionRef.current = setExitDirection;
    }
  }, [setExitDirectionRef]);

  // Measure actual container height
  useEffect(() => {
    const measureHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        if (height > 0 && height !== containerHeight) {
          setContainerHeight(height);
        }
      }
    };

    // Measure on mount and when window resizes
    measureHeight();
    window.addEventListener('resize', measureHeight);
    
    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(measureHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', measureHeight);
      resizeObserver.disconnect();
    };
  }, [containerHeight]);

  // Calculate tile height immediately when mode changes or chatInputHeight changes or container height changes
  useEffect(() => {
    // Skip recalculation if animation is in progress to prevent arrow twitching
    if (isAnimating) return;
    
    // Skip if we don't have a container height measurement yet
    if (containerHeight === 0) {
      return;
    }
    
    // Track if this is triggered by chatInputHeight change
    const heightChanged = chatInputHeight !== lastProcessedHeightRef.current;
    lastProcessedHeightRef.current = chatInputHeight;
      
      // Only reset sizing state when mode changes, not for height adjustments
      const modeChanged = previousModeRef.current !== current.mode;
      if (modeChanged) {
        setIsSizingComplete(false);
      }
      
      // Calculate tile height for mode changes
      if (current.mode) {
        const currentTiles = getTilesForMode(current.mode);
        
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
      // Use actual container height instead of calculated max parent height
      const availableHeight = containerHeight;
            
  // For two-column layout, calculate the number of rows needed
  const tilesPerColumn = Math.ceil(currentTiles / 2);
  
  // Subtract header + margin + padding before distributing remaining height among tiles
  // No gap calculation needed since we use justify-between which handles spacing automatically
  const totalHeaderSpace = headerBlockHeight + headerMarginBottom + columnVerticalPadding;
  const availableForTiles = Math.max(minSubtitleHeight + 10, availableHeight - totalHeaderSpace);
  let tileHeight = Math.floor(availableForTiles / tilesPerColumn);
    
  // Enforce a higher minimum so subtitles can render on large screens
  if (windowHeight >= 700) tileHeight = Math.max(minSubtitleHeight, tileHeight);
      
      // Only apply minimum constraint when absolutely necessary - use all available space
      if (tileHeight < 20) {
        tileHeight = 20;
      } else if (tileHeight > 90) {
        tileHeight = 90; // Cap at 90 for better appearance
      }
      // Use calculated height without additional reduction
  tileHeight = Math.max(35, tileHeight); // retain safeguard but no reduction
      
    // Also update the CSS variable for the container height to match our calculations
    // For two-column layout, use the number of rows (tilesPerColumn) for height calculation
    // The totalNeededHeight is the actual height needed to fit header + all tiles + gaps
    const totalNeededHeight = totalHeaderSpace + (tileHeight * tilesPerColumn) + ((tilesPerColumn - 1) * gap);
        document.documentElement.style.setProperty('--gallery-fixed-height', `${totalNeededHeight}px`);
        
    const baseTileWidth = calculateTileWidth();
    const finalTileWidth = Math.min(tileMaxWidth, Math.max(80, baseTileWidth - tileWidthReduction));
    setTileSize({ width: finalTileWidth, height: tileHeight });
      
      // Mark that first render is complete
      isFirstRenderRef.current = false;
      
      // Wait for DOM to update with new tile sizes before starting animation
      // Only needed for mode changes, not height adjustments
      if (modeChanged) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsSizingComplete(true);
          });
        });
      }
      }
  }, [current.mode, galleryExtraBottomPadding, greetingVisualShift, tileHeightReduction, calculateTileWidth, tileWidthReduction, isAnimating, chatInputHeight, containerHeight, tileMaxWidth]);

  // Update grid variables when mode changes
  useEffect(() => {
    if (!current.mode) return;
    
    const currentTiles = getTilesForMode(current.mode);
    const gridColumns = 2;
    const gridRows = Math.ceil(currentTiles / 2);
    
    document.documentElement.style.setProperty('--gallery-columns', `${gridColumns}`);
    document.documentElement.style.setProperty('--gallery-rows', `${gridRows}`);
  }, [current.mode]);

  // Update exit direction state when mode changes (reads from ref set by HeaderGalleryTitle)
  useEffect(() => {
    setExitDirection(exitDirectionRef.current);
  }, [current.mode, exitDirectionRef]);

  // Handle mode change clicks, -1 is left and 1 is right
  // Calls goToModeWithDirection()
  const arrowModeClick = (direction: number) => {
    if (!current.mode) return; // Guard against null mode
    
    let newIdx;
    const modeIdx = MODES.indexOf(current.mode); // get current mode index
    if (direction === -1) newIdx = (modeIdx - 1 + MODES.length) % MODES.length;
    else newIdx = (modeIdx + 1) % MODES.length;
    
    const newMode = MODES[newIdx] as GalleryMode;
    
    // Set the exit direction in both ref (for cross-component coordination) 
    // and state (for animations) with flushSync to ensure immediate update
    flushSync(() => {
      exitDirectionRef.current = direction;
      setExitDirection(direction);
    });
    
    // Update Redux chat mode state to keep everything in sync
    // Cast is safe because MODES only contains "Ask Chat" and "3M Knowledge"
    dispatch(setChatMode(newMode as any));
    
    // Now trigger the mode change
    if(goToModeWithDirection) goToModeWithDirection(newMode, direction);
  };

  // Simple tile sizing - rectangular tiles longer in width than height
  // Variables moved to tileSize state for responsive behavior
  const gap = 16;

  /** This function adjusts the gallery layout (height for single-column) only on resize/initial render 
   *  It requires adjusting the gallery height, does not change the tile sizes themselves and does not run with a mode switch
   *  Calls getMaxTiles()
  */
  const adjustGalleryLayout = useCallback((containerHeight: number, tileWidth: number, gap: number) => {
    // Use the largest mode (3M Knowledge with 7 tiles) to size the container
    const maxTiles = getMaxTiles();
    
    // For two-column layout, rows = tiles divided by 2 (rounded up)
    const maxRowsForLayout = Math.ceil(maxTiles / 2);
    
    // Calculate what tile height we would need for the largest mode
  // Remove header + margin + padding before distributing space among tiles for two-column height calc
  const totalHeaderSpace = headerBlockHeight + headerMarginBottom + columnVerticalPadding;
  const adjustedContainerHeight = Math.max(60, containerHeight - totalHeaderSpace);
  let idealMaxTileHeight = Math.floor((adjustedContainerHeight - (maxRowsForLayout - 1) * gap) / maxRowsForLayout);
  if (window.innerHeight >= 700) idealMaxTileHeight = Math.max(minSubtitleHeight, idealMaxTileHeight);
    
    // Only apply constraints when absolutely necessary
    let actualMaxTileHeight;
    if (idealMaxTileHeight < 20) {
      actualMaxTileHeight = 20;
    } else if (idealMaxTileHeight > 80) {
      actualMaxTileHeight = 80;
    } else {
      actualMaxTileHeight = idealMaxTileHeight;
    }
    
    // Calculate actual needed height: header space + tiles + gaps between tiles
    const finalGalleryHeight = totalHeaderSpace + (maxRowsForLayout * actualMaxTileHeight) + ((maxRowsForLayout - 1) * gap);
    
    // Enforce minimum height only
    const constrainedGalleryHeight = Math.max(150, finalGalleryHeight);
    
    // Set container dimensions (fixed width for single-column, variable height)
  document.documentElement.style.setProperty('--gallery-fixed-width', `${galleryFixedWidth}px`); // Further reduced fixed width for compact layout
    document.documentElement.style.setProperty('--gallery-fixed-height', `${constrainedGalleryHeight}px`);
  }, []);



  // chatInputHeight changes are now handled in the main tile calculation useEffect above

  // Initialize CSS variables immediately with some safe defaults to prevent flashing on initial render
  React.useLayoutEffect(() => {
  document.documentElement.style.setProperty('--gallery-fixed-width', `${galleryFixedWidth}px`);
    document.documentElement.style.setProperty('--gallery-fixed-height', '400px');
    document.documentElement.style.setProperty('--gallery-columns', '1');
    document.documentElement.style.setProperty('--gallery-rows', '4');
  }, []);

  // Calculate tile size based on window size and whatever mode has the most tiles so that the gallery is a consistent size
  // This avoids layout shifts when moving through the gallery modes
  useEffect(() => {
    // function is wrapped in a useEffect to ensure the event listener is only added on the client side to avoid SSR issues with window being undefined
    // also, the function is defined inside useEffect to avoid recreating it on every render. The same function reference is used for both mounting and unmounting 
    // the event listener for clean up
    
    let rafId: number;

    /** Handle window resize events (not mode switches).
     * Calls getMaxTiles(), calculateAndUpdateLayout()
     */
    function handleResize() {
      // Skip on first render to prevent adjustment after initial mode selection
      if (isFirstRenderRef.current && previousModeRef.current === null) {
        return;
      }
      
      // Cancel any pending updates for smoothness
      cancelAnimationFrame(rafId);
      
      // Do immediate calculation for smooth resizing
      const maxTiles = getMaxTiles(); // find which mode has the most number of tiles and use that for sizing
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Immediate update with current layout
      rafId = requestAnimationFrame(() => {
        calculateAndUpdateLayout(windowWidth, windowHeight, maxTiles);
      });
    }
    
    /** Calculate and update layout based on window size and max tiles in any mode
     * For single-column layout, focus on height calculations
     */
    function calculateAndUpdateLayout(windowWidth: number, windowHeight: number, maxTiles: number) {
        // Update responsive values
        setWindowWidth(windowWidth);
        const newResponsivePadding = getResponsivePadding();
        setResponsivePadding(newResponsivePadding);
        
        // Calculate tile height based on available window height - USE actual chatInputHeight
        const baseMaxParentHeight = getResponsiveMaxParentHeight(windowWidth, windowHeight, chatInputHeight);
        const adjustedMaxParentHeight = baseMaxParentHeight - galleryExtraBottomPadding - greetingVisualShift;

  // For two-column layout, calculate rows as half the tiles (rounded up)
  const tilesPerColumn = Math.ceil(maxTiles / 2);
  
  // Resize path: subtract header + margin + padding before tile distribution to keep internal content bounded
  const totalHeaderSpace = headerBlockHeight + headerMarginBottom + columnVerticalPadding;
  const availableResizeHeight = Math.max(60, adjustedMaxParentHeight - totalHeaderSpace);
  let tileHeight = Math.floor((availableResizeHeight - (tilesPerColumn - 1) * gap) / tilesPerColumn);
  if (windowHeight >= 700) tileHeight = Math.max(minSubtitleHeight, tileHeight);
  tileHeight = Math.max(35, Math.min(140, tileHeight)); // Cap at 140 for better appearance

        const tileWidth = calculateTileWidth();
  const reducedTileWidth = Math.min(tileMaxWidth, Math.max(80, tileWidth - tileWidthReduction));
        setTileSize({ width: reducedTileWidth, height: tileHeight });

        // Don't call adjustGalleryLayout - the main useEffect handles container height
        // During animation: do nothing - let existing properties stay unchanged
      }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [isAnimating, adjustGalleryLayout, calculateTileWidth, getResponsivePadding, chatInputHeight, tileMaxWidth]);

  // Handle mode-specific tile sizing after animation completes
  useEffect(() => {
    if (isAnimating) {
      // For first selection (no previous mode), no delay needed since there's no animation
      if (previousModeRef.current === null) {
        setIsAnimating(false);
        // Don't call adjustTilesForCurrentMode() here - the main mode-change useEffect handles it
      } else {
        // Wait for animation to complete (300ms) then adjust tile sizes
        const timer = setTimeout(() => {
          setIsAnimating(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isAnimating, setIsAnimating]);

  // Handle animation state
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isAnimating, setIsAnimating]);

  const tiles = getTileDataForMode(current.mode);

  // Check if screen is too small for gallery
  const [isScreenTooSmall, setIsScreenTooSmall] = useState(false);
  
  // This is an edge case for web, TODO: build out another case for specifically mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsScreenTooSmall((window.innerWidth < 600 && (deviceType === 'desktop'))); // Hide gallery below 600px only for desktop
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [deviceType]);

  // render nothing on small screens
  if (isScreenTooSmall) {
    return null;
  }

  // Early return if no tiles to avoid crashes (but allow first selection to show fade-in)
  if (current.mode && (!tiles || tiles.try.length === 0)) {
    return null;
  }

  // For null mode, render container but no content (enables fade-in animation)
  const shouldShowContent = current.mode && tiles && tiles.try.length > 0;
  // Show arrows when there are multiple modes available
  const shouldShowArrows = MODES.length > 1;

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col items-center mb-8 pb-8 relative">
      {/* Gallery content for the selected mode, centered and full width, with arrows tightly beside tiles */}
      <div className="relative flex flex-row items-center justify-center w-full h-full">
        <div
          ref={galleryRef}
          className="relative flex items-start justify-center w-full"
          id="gallery-tiles-container"
          style={{ 
            width: '100%',
            height: '100%',
            margin: '0',
            // Align outer container padding with tile width calculation (responsive + EXTRA_GALLERY_SIDE_PADDING)
            paddingLeft: `${responsivePadding + EXTRA_GALLERY_SIDE_PADDING}px`,
            paddingRight: `${responsivePadding + EXTRA_GALLERY_SIDE_PADDING}px`
          }}
        >
          {/* Animated gallery container */}
          <AnimatePresence mode="wait" initial={false}>
            {/* TODO: Bring back when we are ready for multiple modes, for now always show gallery */}
            {/* {shouldShowContent && current.mode && ( */}
            {true && (
              <motion.div
                key={`${current.mode}-dir${current.direction}`}
                initial={
                  // If coming from null (first selection), start invisible and wait for sizing
                  previousModeRef.current === null 
                    ? { x: 0, opacity: 0 }
                    : { x: current.direction === 1 ? 300 : -300, opacity: 0 }
                }
                animate={{ 
                  x: 0, 
                  opacity: 1  // Always show - no conditional opacity
                }}
                exit={{ 
                  x: exitDirection === -1 ? 300 : -300, 
                  opacity: 0 
                }}
                transition={
                  // Smooth fade-in for first selection
                  previousModeRef.current === null
                    ? { duration: 0.3, ease: "easeOut" }
                    : { type: "spring", stiffness: 400, damping: 40, duration: 0.35 }
                }
                onAnimationComplete={() => {
                  // Update previous mode for next animation
                  previousModeRef.current = current.mode;
                  setIsAnimating(false);
                }}
                className="w-full h-full flex items-start justify-center box-border"
                style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  margin: 0,
                  maxWidth: '100%'
                }}
              >
                {/* Two column layout: Give It a Try */}
                <div className="flex flex-col items-center h-full" style={{ flex: '1 1 0%', minWidth: 0, maxWidth: '100%', overflow: 'visible' }}>
                  {/* Gallery Title */}
                  {(deviceType==='desktop') && (
                    <div className="flex items-center justify-center mb-4">
                      {/* Sparkles icon */}
                      <span className="mr-2" style={{ color: titleColor }}>
                        {React.createElement(HiOutlineSparkles as React.ComponentType<any>, { size: 18 })}
                      </span>
                      <span className="font-bold" style={{ fontSize: '1rem', paddingTop: '2px', color: titleColor }}>Quick start – Prompt ideas to inspire you</span>
                    </div>
                  )}

                  {/* Wrapper for tiles only - arrows will be centered relative to this */}
                  <div className="relative w-full flex-1 flex flex-col" id="tiles-only-wrapper">
                    {/* Rows of two tiles. A trailing odd tile is its own row inside the same
                        stack so it shares the row sizing/gap instead of hanging below it. */}
                    <div className="flex flex-col gap-3 w-full flex-1">
                      {Array.from({ length: Math.ceil(tiles.try.length / 2) }, (_, rowIndex) => {
                        const rowTiles = tiles.try.slice(rowIndex * 2, rowIndex * 2 + 2) as GalleryTileProps[];
                        return (
                          // TODO: verify tile max height — dev uses max-h-[75px]
                          <div
                            key={`tile-row-${rowIndex}`}
                            className="flex flex-row gap-8 w-full flex-1 min-h-0 max-h-[95px]"
                            style={{ justifyContent: 'center', alignItems: 'stretch' }}
                          >
                            {rowTiles.map((tile: GalleryTileProps, colIndex: number) => (
                              <GalleryTile
                                key={`${tile.title}-${rowIndex * 2 + colIndex}`}
                                mode={mode}
                                {...tile}
                                subtitle={tileSize.height >= 35 ? tile.subtitle : ''}
                                width={calculateTileWidth()}
                                onClick={() => {
                                  if (tile.prompt && onTileClick) {
                                    onTileClick(tile.prompt, tile.tool);
                                  }
                                }}
                                style={{
                                  fontSize: `clamp(0.65rem, ${windowWidth * 0.0007}rem, 1rem)`,
                                  height: '100%',
                                  ...tile.style
                                }}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  
                    {/* Left arrow - show when multiple modes are available */}
                    {/* {shouldShowArrows && (
                      <button
                        className="absolute z-10 text-[var(--color-accent-hover)] hover:text-[var(--color-text)]"
                        style={{ 
                          // Position arrow so its right edge (left + ARROW_ICON_WIDTH) maintains ARROW_SIDE_GAP from tile content start
                          left: `${-(ARROW_INSET_ADJUST + ARROW_ICON_WIDTH + ARROW_SIDE_GAP)}px`,
                          // Center vertically within tiles wrapper only
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                        onClick={() => arrowModeClick(-1)}
                        aria-label="Previous mode"
                      >
                        <ChevronLeftIcon width={ARROW_ICON_WIDTH} height={ARROW_ICON_WIDTH} />
                      </button>
                    )} */}
                    
                    {/* Right arrow - show when multiple modes are available */}
                    {/* {shouldShowArrows && (
                      <button
                        className="absolute z-10 text-[var(--color-accent-hover)] hover:text-[var(--color-text)]"
                        style={{ 
                          // Position arrow so its left edge maintains ARROW_SIDE_GAP from tile content end
                          right: `${-(ARROW_INSET_ADJUST + ARROW_ICON_WIDTH + ARROW_SIDE_GAP)}px`,
                          // Center vertically within tiles wrapper only
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                        onClick={() => arrowModeClick(1)}
                        aria-label="Next mode"a
                      >
                        <ChevronRightIcon width={ARROW_ICON_WIDTH} height={ARROW_ICON_WIDTH} />
                      </button>
                    )} */}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
